import { useState, useEffect, useMemo } from 'react';
import { LogOut, Calendar as CalendarIcon, MapPin, Car, X, Plus, Navigation, Star, UserPlus, CheckCircle, ClipboardList, Filter, Eye, EyeOff, Megaphone, Trash2, Contact, User } from 'lucide-react';
import MapComponent from '../components/MapComponent';
import CustomerPins from '../components/CustomerPins';
import Directions from '../components/Directions';
import SiteSurveyModal from '../components/SiteSurveyModal';
import NewLeadModal from '../components/NewLeadModal';

import { AdvancedMarker, Pin, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const [dbFeatures, setDbFeatures] = useState([]);
  const [selectedFeatureInfo, setSelectedFeatureInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [teamColors, setTeamColors] = useState({});
  const [customers, setCustomers] = useState([]);
  const [showCustomers, setShowCustomers] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showCustomerNames, setShowCustomerNames] = useState(true);
  const [savedPlaces, setSavedPlaces] = useState([]);
  const [dashboardLeads, setDashboardLeads] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const routesLib = useMapsLibrary('routes');
  const [placesService, setPlacesService] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);

  const [focusedCustomer, setFocusedCustomer] = useState(null);
  const [tempGooglePin, setTempGooglePin] = useState(null);
  
  // Site Survey State
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [activeSurveyPlace, setActiveSurveyPlace] = useState(null);
  
  // New Lead Modal State
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  
  // Trip Planning State
  const [tripStops, setTripStops] = useState([]);
  const [activeRouteStops, setActiveRouteStops] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);
  const [isTripIsolated, setIsTripIsolated] = useState(false);
  
  const [travelTimes, setTravelTimes] = useState({});
  const [showTerritories, setShowTerritories] = useState(true);
  const [isTeamFilterOpen, setIsTeamFilterOpen] = useState(false);
  const [hiddenTeams, setHiddenTeams] = useState([]);
  const [hiddenSales, setHiddenSales] = useState([]);
  const [hiddenAfterSales, setHiddenAfterSales] = useState([]);
  const [salesCodeToTeamName, setSalesCodeToTeamName] = useState({});
  const [allUsers, setAllUsers] = useState([]);

  const filterStructure = useMemo(() => {
    const structure = {};
    
    dbFeatures.forEach(f => {
       const team = f.properties.team_name;
       if (team && !structure[team]) {
          structure[team] = { sales: {}, afterSales: {} };
       }
    });

    allUsers.forEach(u => {
      if (!u.sales_code) return;
      const teamName = salesCodeToTeamName[u.sales_code];
      if (!teamName) return;
      
      if (!structure[teamName]) structure[teamName] = { sales: {}, afterSales: {} };
      
      if (u.role === 'sale' || u.role === 'sale_manager') {
         structure[teamName].sales[u.sales_code] = 0;
      }
      if (u.role === 'after_sale') {
         structure[teamName].afterSales[u.sales_code] = 0;
      }
    });

    customers.forEach(c => {
      const saleTeam = salesCodeToTeamName[c.update_sales_code];
      const afterSaleTeam = salesCodeToTeamName[c.update_after_sales_code];

      // Add to Sale Team
      if (c.update_sales_code && saleTeam) {
        if (!structure[saleTeam]) structure[saleTeam] = { sales: {}, afterSales: {} };
        if (structure[saleTeam].sales[c.update_sales_code] === undefined) {
           structure[saleTeam].sales[c.update_sales_code] = 0;
        }
        structure[saleTeam].sales[c.update_sales_code]++;
      }
      
      // Add to After Sale Team
      if (c.update_after_sales_code && afterSaleTeam) {
        if (!structure[afterSaleTeam]) structure[afterSaleTeam] = { sales: {}, afterSales: {} };
        if (structure[afterSaleTeam].afterSales[c.update_after_sales_code] === undefined) {
           structure[afterSaleTeam].afterSales[c.update_after_sales_code] = 0;
        }
        structure[afterSaleTeam].afterSales[c.update_after_sales_code]++;
      }
    });
    
    dbFeatures.forEach(f => {
       const team = f.properties.team_name;
       if (team && !structure[team]) {
          structure[team] = { sales: {}, afterSales: {} };
       }
    });

    Object.keys(structure).forEach(t => {
      structure[t].sales = Object.entries(structure[t].sales)
                                 .map(([code, count]) => ({ code, count }))
                                 .sort((a, b) => a.code.localeCompare(b.code));
      structure[t].afterSales = Object.entries(structure[t].afterSales)
                                      .map(([code, count]) => ({ code, count }))
                                      .sort((a, b) => a.code.localeCompare(b.code));
    });
    
    return structure;
  }, [dbFeatures, customers, allUsers, salesCodeToTeamName]);

  const toggleTeamVisibility = (team) => {
    setHiddenTeams(prev => prev.includes(team) ? prev.filter(t => t !== team) : [...prev, team]);
  };
  
  const toggleSaleVisibility = (team, sale) => {
    const key = `${team}_${sale}`;
    setHiddenSales(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleAfterSaleVisibility = (team, afterSale) => {
    const key = `${team}_${afterSale}`;
    setHiddenAfterSales(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleAllSalesForTeam = (team, checked) => {
    const allSalesKeys = filterStructure[team].sales.map(s => `${team}_${s.code}`);
    if (checked) {
      setHiddenSales(prev => prev.filter(k => !allSalesKeys.includes(k)));
    } else {
      setHiddenSales(prev => [...new Set([...prev, ...allSalesKeys])]);
    }
  };

  const toggleAllAfterSalesForTeam = (team, checked) => {
    const allAfterSalesKeys = filterStructure[team].afterSales.map(s => `${team}_${s.code}`);
    if (checked) {
      setHiddenAfterSales(prev => prev.filter(k => !allAfterSalesKeys.includes(k)));
    } else {
      setHiddenAfterSales(prev => [...new Set([...prev, ...allAfterSalesKeys])]);
    }
  };

  useEffect(() => {
    if (!user) return;
    
    setLoadingStatus('กำลังโหลดเขตการขายและหมุดลูกค้า...');

    // Parallel fetch: trigger all requests simultaneously
    const teamsPromise = fetch('/api/teams').then(res => res.json()).catch(err => ({ status: 'error', data: [] }));
    const usersPromise = fetch('/api/users').then(res => res.json()).catch(err => ({ status: 'error', data: [] }));
    const areasPromise = fetch('/api/areas').then(res => res.json()).catch(err => ({ status: 'error', data: [] }));
    const customersPromise = fetch('/api/customers').then(res => res.json()).catch(err => ({ status: 'error', data: [] }));

    // Prepare metadata as soon as teams and users resolve
    const metadataPromise = Promise.all([teamsPromise, usersPromise])
      .then(([teamsData, usersData]) => {
        const colors = {};
        const teamIdToName = {};
        
        if (teamsData.status === 'success') {
          (teamsData.data || []).forEach(t => {
            teamIdToName[t.id] = t.team_name;
            if (t.color) {
              colors[t.id] = t.color;
              colors[t.team_name] = t.color;
            }
          });
        }

        const userToTeamMap = {};
        if (usersData.status === 'success') {
          setAllUsers(usersData.data || []);
          (usersData.data || []).forEach(u => {
            if (u.sales_code && u.team_id && teamIdToName[u.team_id]) {
              userToTeamMap[u.sales_code] = teamIdToName[u.team_id];
            }
          });
        }
        setSalesCodeToTeamName(userToTeamMap);

        return { colors, teamIdToName, userToTeamMap };
      });

    // Areas rendering - Stream onto map as soon as areas and metadata arrive
    Promise.all([metadataPromise, areasPromise])
      .then(([{ colors }, areasData]) => {
        if (areasData.status === 'success') {
          const updatedColors = { ...colors };
          const features = (areasData.data || []).map(area => {
            try {
              const feature = JSON.parse(area.geojson_data);
              feature.properties = feature.properties || {};
              feature.properties.team_name = area.team_name;
              feature.properties.team_id = area.team_id;

              if (area.team_color) {
                updatedColors[area.team_id] = area.team_color;
                if (area.team_name) {
                  updatedColors[area.team_name] = area.team_color;
                }
              }
              
              return feature;
            } catch (e) {
              return null;
            }
          }).filter(Boolean);

          setTeamColors(updatedColors);
          setDbFeatures(features);
        }
      })
      .catch(err => console.error("Error loading areas:", err));

    // Customers rendering - Process and stream onto map as soon as customers and metadata arrive
    Promise.all([metadataPromise, customersPromise])
      .then(([{ teamIdToName, userToTeamMap }, customersData]) => {
        if (customersData.status === 'success') {
          const processedCustomers = (customersData.data || []).map(c => ({
              ...c,
              cd_no: c.cd_no || `CUST_${c.id}`,
              team_name: teamIdToName[c.team_id] || userToTeamMap[c.update_sales_code] || userToTeamMap[c.update_after_sales_code] || ''
          }));

          if (user.role !== 'admin' && user.role !== 'super_admin' && user.team_name) {
             setCustomers(processedCustomers.filter(c => 
               userToTeamMap[c.update_sales_code] === user.team_name || 
               userToTeamMap[c.update_after_sales_code] === user.team_name
             ));
          } else {
             setCustomers(processedCustomers);
          }
        }
      })
      .catch(err => console.error("Error loading customers:", err))
      .finally(() => {
        setLoadingStatus('');
      });

    // Fetch saved places and leads
    fetchSavedPlaces();
    fetchDashboardLeads();

    // Get User's Current Location
    let locationSet = false;
    
    const fallbackToIP = async () => {
      if (locationSet) return;
      try {
        const res = await fetch('https://get.geojs.io/v1/ip/geo.json');
        const data = await res.json();
        if (data.latitude && data.longitude) {
          locationSet = true;
          setUserLocation({ lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) });
        }
      } catch (err) {
        console.warn("IP Geolocation fallback failed:", err);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!locationSet) {
            locationSet = true;
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          }
        },
        (error) => {
          console.warn("Geolocation error in Dashboard:", error);
          fallbackToIP();
        },
        { enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 }
      );
      
      setTimeout(() => { fallbackToIP(); }, 4000);
    } else {
      fallbackToIP();
    }
  }, [user ? user.id : null]);

  // Initialize PlacesService & DirectionsService
  useEffect(() => {
    if (placesLib && map) {
      setPlacesService(new placesLib.PlacesService(map));
    }
    if (routesLib) {
      setDirectionsService(new routesLib.DirectionsService());
    }
  }, [placesLib, routesLib, map]);

  // Calculate Travel Times for Plan to Visit
  useEffect(() => {
    const plannedTasks = savedPlaces.filter(p => p.is_planned == 1);
    if (plannedTasks.length === 0 || !userLocation || !directionsService || !window.google) return;

    const origin = new window.google.maps.LatLng(userLocation.lat, userLocation.lng);
    
    const fetchAllRoutes = async () => {
      const newTravelTimes = {};

      for (const task of plannedTasks) {
        try {
          const destination = new window.google.maps.LatLng(parseFloat(task.lat), parseFloat(task.lng));
          const response = await directionsService.route({
            origin: origin,
            destination: destination,
            travelMode: window.google.maps.TravelMode.DRIVING
          });

          if (response.routes && response.routes[0] && response.routes[0].legs[0]) {
            const leg = response.routes[0].legs[0];
            newTravelTimes[task.id] = {
              distance: leg.distance.text,
              duration: leg.duration.text
            };
          }
        } catch (err) {
          console.error("Directions Error for task", task.id, err);
        }
      }

      setTravelTimes(newTravelTimes);
    };

    fetchAllRoutes();
  }, [savedPlaces, userLocation, directionsService]);

  // Handle Global Search Results
  useEffect(() => {
    const searchResult = location.state?.searchResult;
    if (searchResult && map) {
      if (searchResult.type === 'database') {
        const c = searchResult.data;
        const lat = parseFloat(c.lat);
        const lng = parseFloat(c.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          map.panTo({ lat, lng });
          map.setZoom(15);
          setTimeout(() => {
            setFocusedCustomer(c);
            setTempGooglePin(null);
          }, 800);
        }
      } else if (searchResult.type === 'saved_place') {
        const p = searchResult.data;
        const lat = parseFloat(p.lat);
        const lng = parseFloat(p.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          map.panTo({ lat, lng });
          map.setZoom(15);
          setTimeout(() => {
            setTempGooglePin({
              position: { lat, lng },
              name: p.name,
              address: p.address,
              cd_no: `GOOGLE_${p.place_id}`,
              search_name: p.name,
              lat: lat,
              lng: lng
            });
            setFocusedCustomer(null);
          }, 800);
        }
      } else if (searchResult.type === 'google' && placesService) {
        const place = searchResult.data;
        placesService.getDetails({ 
          placeId: place.place_id, 
          fields: ['name', 'geometry', 'formatted_address']
        }, (result, status) => {
          if (status === 'OK' && result && result.geometry && result.geometry.location) {
            const pos = { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() };
            map.panTo(pos);
            map.setZoom(15);
            setTimeout(() => {
              setTempGooglePin({
                position: pos,
                name: result.name,
                address: result.formatted_address,
                cd_no: `GOOGLE_${place.place_id}`, // Generate fake cd_no for trip planner
                search_name: result.name,
                lat: pos.lat,
                lng: pos.lng
              });
              setFocusedCustomer(null);
            }, 800);
          }
        });
      }

      // Clear state so it doesn't re-trigger on un-related re-renders
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, map, placesService, navigate]);

  const fetchSavedPlaces = () => {
    fetch('/api/google_places')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setSavedPlaces(data.data);
        }
      })
      .catch(err => console.error("Error fetching saved places:", err));
  };

  const handleDeleteFavorite = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("คุณต้องการลบรายการโปรดนี้ใช่หรือไม่?")) return;
    try {
        const response = await fetch(`/api/google_places/${id}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        if (data.status === 'success') {
            fetchSavedPlaces();
        } else {
            alert('ลบไม่สำเร็จ: ' + data.message);
        }
    } catch (err) {
        console.error('Error deleting favorite:', err);
        alert('เกิดข้อผิดพลาดในการลบรายการโปรด');
    }
  };

  const fetchDashboardLeads = () => {
    if (!user) return;
    const url = `/api/leads?email=${user.google_email}&role=${user.role}&team_name=${user.team_name}`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          // Filter only New leads
          const newLeads = data.data.filter(l => l.lead_status === 'New');
          setDashboardLeads(newLeads);
        }
      })
      .catch(err => console.error("Error fetching dashboard leads:", err));
  };

  const saveGooglePlace = (type) => {
    if (!tempGooglePin || !user) return;
    
    fetch('/api/google_places', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        place_id: tempGooglePin.cd_no.replace('GOOGLE_', ''),
        name: tempGooglePin.name,
        address: tempGooglePin.address,
        lat: String(tempGooglePin.lat),
        lng: String(tempGooglePin.lng),
        type: type,
        saved_by_email: user.google_email,
        saved_by_name: user.name || user.google_email,
        saved_by_team: user.team_name || ''
      })
    })
    .then(res => res.json())
    .then(data => {
      if (data.status === 'success') {
        alert(type === 'favorite' ? '⭐ บันทึกรายการโปรดสำเร็จ!' : '👤 บันทึกลิส Sales Lead สำเร็จ!');
        fetchSavedPlaces();
      } else {
        alert('เกิดข้อผิดพลาด: ' + data.message);
      }
    })
    .catch(err => {
      console.error(err);
      alert('ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่');
    });
  };

  const updatePlannedDate = (id, date) => {
    fetch('/api/google_places/' + id, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: id,
        action: 'plan_visit',
        planned_date: date
      })
    })
    .then(res => res.json())
    .then(data => {
      if (data.status === 'success') {
        fetchSavedPlaces();
      } else {
        alert('เกิดข้อผิดพลาด: ' + data.message);
      }
    })
    .catch(err => console.error(err));
  };

  const handlePlaceClick = (p) => {
    if (!map) return;
    const lat = parseFloat(p.lat || p.latitude);
    const lng = parseFloat(p.lng || p.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    
    const pos = { lat, lng };
    map.panTo(pos);
    map.setZoom(15);
    
    let placeType = null;
    if (p.lead_status === 'New') placeType = 'lead';
    else if (p.is_visited == 1) placeType = 'last_visited';
    else if (p.is_planned == 1) placeType = 'planned';
    else if (p.type === 'favorite') placeType = 'favorite';
    else if (p.type === 'new_customer') placeType = 'new_customer';
    
    const isLead = placeType === 'lead';
    
    setTimeout(() => {
      setTempGooglePin({
        position: pos,
        name: isLead ? (p.company_name_th || p.company_name_en || p.customer_name || 'ไม่ระบุชื่อบริษัท') : p.name,
        address: isLead ? (p.address || p.province || p.region || p.location || '') : p.address,
        cd_no: isLead ? `LEAD_${p.id}` : `GOOGLE_${p.place_id}`,
        search_name: isLead ? (p.company_name_th || p.company_name_en || p.customer_name) : p.name,
        lat: lat,
        lng: lng,
        placeType: placeType
      });
      setFocusedCustomer(null);
    }, 800);
  };

  const favorites = savedPlaces.filter(p => p.type === 'favorite' && p.is_planned == 0 && p.is_visited != 1 && p.saved_by_email === user?.google_email);
  const newPlaces = savedPlaces.filter(p => p.type === 'new_customer' && p.is_planned == 0 && p.is_visited != 1 && (user?.role === 'admin' || user?.role === 'super_admin' || p.saved_by_team === user?.team_name));
  const tasks = savedPlaces.filter(p => p.is_planned == 1 && p.saved_by_email === user?.google_email);
  const lastVisited = savedPlaces.filter(p => p.is_visited == 1 && p.saved_by_email === user?.google_email);

  if (!user) {
    return <div style={{ padding: '2rem' }}>Please log in to view dashboard.</div>;
  }

  // Check if unassigned
  if (!user.team_id && user.role !== 'admin' && user.role !== 'super_admin') {
    return (
      <div className="card" style={{ maxWidth: '600px', margin: '2rem auto', textAlign: 'center', borderColor: '#fca5a5', backgroundColor: '#fef2f2' }}>
        <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>⚠️ ไม่พบข้อมูลเขตพื้นที่การขาย</h2>
        <p style={{ color: '#7f1d1d', fontSize: '1.1rem' }}>
          คุณยังไม่ถูกระบุว่าอยู่ในเขตการขายไหน รบกวนติดต่อ admin
        </p>
      </div>
    );
  }

  const openGoogleMapsApp = () => {
    if (!userLocation || activeRouteStops.length === 0) return;
    
    const originStr = `${userLocation.lat},${userLocation.lng}`;
    const destStop = activeRouteStops[activeRouteStops.length - 1];
    const destinationStr = `${destStop.lat},${destStop.lng}`;
    
    let url = `https://www.google.com/maps/dir/?api=1&origin=${originStr}&destination=${destinationStr}&travelmode=driving`;
    
    if (activeRouteStops.length > 1) {
      const waypointsStr = activeRouteStops.slice(0, -1).map(stop => `${stop.lat},${stop.lng}`).join('|');
      url += `&waypoints=${waypointsStr}`;
    }
    
    window.open(url, '_blank');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
        <h2 style={{ margin: 0, color: 'var(--color-text-main)', fontSize: '1.4rem' }}>
          {user.role === 'admin' || user.role === 'super_admin' ? 'Overview' : `Team: ${user.team_name || 'All'}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#475569', cursor: 'pointer', backgroundColor: 'white', padding: '0.3rem 0.6rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <input 
              type="checkbox" 
              checked={showTerritories} 
              onChange={(e) => setShowTerritories(e.target.checked)} 
              style={{ margin: 0, cursor: 'pointer' }}
            />
            พื้นที่การขาย
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#475569', cursor: 'pointer', backgroundColor: 'white', padding: '0.3rem 0.6rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <input 
              type="checkbox" 
              checked={showCustomers} 
              onChange={(e) => setShowCustomers(e.target.checked)} 
              style={{ margin: 0, cursor: 'pointer' }}
            />
            ลูกค้า
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#475569', cursor: 'pointer', backgroundColor: 'white', padding: '0.3rem 0.6rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <input 
              type="checkbox" 
              checked={showLabels} 
              onChange={(e) => setShowLabels(e.target.checked)} 
              style={{ margin: 0, cursor: 'pointer' }}
            />
            รหัสเซลล์บนหมุด
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#475569', cursor: 'pointer', backgroundColor: 'white', padding: '0.3rem 0.6rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <input 
              type="checkbox" 
              checked={showCustomerNames} 
              onChange={(e) => setShowCustomerNames(e.target.checked)} 
              style={{ margin: 0, cursor: 'pointer' }}
            />
            ชื่อลูกค้าข้างหมุด
          </label>
          <div style={{ position: 'relative' }}>
            <button 
              onClick={() => setIsTeamFilterOpen(!isTeamFilterOpen)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#475569', cursor: 'pointer', backgroundColor: 'white', padding: '0.4rem 0.6rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
            >
              <Filter size={14} /> กรองตามเขต
            </button>
            
            {isTeamFilterOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '4px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', padding: '0.5rem', zIndex: 50, minWidth: '220px', maxHeight: '400px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>เลือกเขตและผู้ดูแล</span>
                  <button 
                    onClick={() => {
                      if (hiddenTeams.length === Object.keys(filterStructure).length) {
                         setHiddenTeams([]); // Show all
                      } else {
                         setHiddenTeams(Object.keys(filterStructure)); // Hide all
                      }
                    }}
                    style={{ fontSize: '0.75rem', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    {hiddenTeams.length === Object.keys(filterStructure).length ? 'แสดงทั้งหมด' : 'ซ่อนทั้งหมด'}
                  </button>
                </div>
                
                {Object.keys(filterStructure).sort().map(team => (
                  <div key={team} style={{ marginBottom: '0.5rem', borderBottom: '1px dashed #e2e8f0', paddingBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#1e293b', cursor: 'pointer', padding: '0.25rem 0', fontWeight: 'bold' }}>
                      <input 
                        type="checkbox" 
                        checked={!hiddenTeams.includes(team)}
                        onChange={() => toggleTeamVisibility(team)}
                        style={{ margin: 0, cursor: 'pointer' }}
                      />
                      <div style={{ width: '12px', height: '12px', backgroundColor: teamColors[team] || '#cbd5e1', borderRadius: '2px' }}></div>
                      {team}
                    </label>
                    
                    {!hiddenTeams.includes(team) && filterStructure[team].sales.length > 0 && (
                      <div style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.7rem', color: '#64748b', fontWeight: '600', cursor: 'pointer', marginBottom: '0.2rem' }}>
                          <input 
                            type="checkbox" 
                            checked={filterStructure[team].sales.every(s => !hiddenSales.includes(`${team}_${s.code}`))}
                            onChange={(e) => toggleAllSalesForTeam(team, e.target.checked)}
                            style={{ margin: 0, cursor: 'pointer' }}
                          />
                          Sales:
                        </label>
                        {filterStructure[team].sales.map(saleObj => (
                          <label key={`sale_${team}_${saleObj.code}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#475569', cursor: 'pointer', padding: '0.15rem 0' }}>
                            <input 
                              type="checkbox" 
                              checked={!hiddenSales.includes(`${team}_${saleObj.code}`)}
                              onChange={() => toggleSaleVisibility(team, saleObj.code)}
                              style={{ margin: 0, cursor: 'pointer' }}
                            />
                            {saleObj.code} <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>({saleObj.count})</span>
                          </label>
                        ))}
                      </div>
                    )}
                    
                    {!hiddenTeams.includes(team) && filterStructure[team].afterSales.length > 0 && (
                      <div style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.7rem', color: '#64748b', fontWeight: '600', cursor: 'pointer', marginBottom: '0.2rem' }}>
                          <input 
                            type="checkbox" 
                            checked={filterStructure[team].afterSales.every(s => !hiddenAfterSales.includes(`${team}_${s.code}`))}
                            onChange={(e) => toggleAllAfterSalesForTeam(team, e.target.checked)}
                            style={{ margin: 0, cursor: 'pointer' }}
                          />
                          After Sales:
                        </label>
                        {filterStructure[team].afterSales.map(afterObj => (
                          <label key={`after_${team}_${afterObj.code}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#475569', cursor: 'pointer', padding: '0.15rem 0' }}>
                            <input 
                              type="checkbox" 
                              checked={!hiddenAfterSales.includes(`${team}_${afterObj.code}`)}
                              onChange={() => toggleAfterSaleVisibility(team, afterObj.code)}
                              style={{ margin: 0, cursor: 'pointer' }}
                            />
                            {afterObj.code} <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>({afterObj.count})</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="dashboard-layout" style={{ display: 'flex', flex: 1, minHeight: '500px', gap: '1rem' }}>
        <div style={{ flex: 1, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {loadingStatus && (
            <div style={{
              position: 'absolute',
              top: '16px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 20,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: '8px 18px',
              borderRadius: '30px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#1e293b',
              border: '2px solid #bfdbfe',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none'
            }}>
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid #bfdbfe',
                borderTop: '2px solid #2563eb',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span>{loadingStatus}</span>
              <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
              `}</style>
            </div>
          )}
        <MapComponent 
          importedFeatures={showTerritories ? dbFeatures : []} 
          teamColors={teamColors}
          onFeatureSelect={(feature) => {
            setSelectedFeatureInfo({
              teamName: feature.getProperty('team_name'),
              areaName: feature.getProperty('name') || 'Imported Area'
            });
          }}
        >
          {showCustomers && (
            <CustomerPins 
            customers={customers.filter(c => {
               if (isTripIsolated && tripStops.length > 0) {
                 return tripStops.some(s => s.cd_no === c.cd_no);
               }
               
               const saleTeam = salesCodeToTeamName[c.update_sales_code];
               const afterSaleTeam = salesCodeToTeamName[c.update_after_sales_code];
               
               const isSaleHidden = saleTeam && hiddenTeams.includes(saleTeam);
               const isAfterSaleHidden = afterSaleTeam && hiddenTeams.includes(afterSaleTeam);
               
               if (!c.update_sales_code && !c.update_after_sales_code) return true;
               if (c.update_sales_code && c.update_after_sales_code) {
                   return !(isSaleHidden && isAfterSaleHidden);
               }
               if (c.update_sales_code) return !isSaleHidden;
               if (c.update_after_sales_code) return !isAfterSaleHidden;
               
               return true;
            })} 
            salesCodeToTeamName={salesCodeToTeamName} 
            hiddenTeams={hiddenTeams}
            hiddenSales={hiddenSales}
            hiddenAfterSales={hiddenAfterSales}
            showLabels={showLabels}
            showCustomerNames={showCustomerNames}
            teamColors={teamColors} 
            focusedCustomer={focusedCustomer}
            onClearFocus={() => setFocusedCustomer(null)}
            onAddToTrip={(customer) => {
              if (tripStops.find(s => s.cd_no === customer.cd_no)) {
                alert("ลูกค้ารายนี้อยู่ในทริปแล้ว");
                return;
              }
              if (tripStops.length >= 24) {
                alert("เพิ่มจุดแวะได้สูงสุด 24 จุดครับ");
                return;
              }
              setTripStops([...tripStops, customer]);
            }}
          />
          )}
          
          {tempGooglePin && (
            <>
              <AdvancedMarker position={tempGooglePin.position} title={tempGooglePin.name} onClick={() => {}}>
                <Pin background="#f97316" glyphColor="#ffffff" borderColor="#ffffff" scale={1.2} />
              </AdvancedMarker>
              <InfoWindow position={tempGooglePin.position} pixelOffset={[0, -30]} onCloseClick={() => setTempGooglePin(null)}>
                <div style={{ maxWidth: '250px', overflow: 'hidden', borderRadius: '6px', backgroundColor: '#ffffff' }}>
                  {tempGooglePin.placeType === 'lead' && (
                    <div style={{ width: '100%', padding: '0.5rem 0.5rem', fontSize: '0.8rem', color: '#4338ca', textAlign: 'center', fontWeight: '600', backgroundColor: '#eef2ff', borderBottom: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      📋 Marketing Lead
                    </div>
                  )}
                  {tempGooglePin.placeType === 'favorite' && (
                    <div style={{ width: '100%', padding: '0.5rem 0.5rem', fontSize: '0.8rem', color: '#b45309', textAlign: 'center', fontWeight: '600', backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      ⭐ รายการโปรด
                    </div>
                  )}
                  {tempGooglePin.placeType === 'new_customer' && (
                    <div style={{ width: '100%', padding: '0.5rem 0.5rem', fontSize: '0.8rem', color: '#047857', textAlign: 'center', fontWeight: '600', backgroundColor: '#ecfdf5', borderBottom: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      👤 Sales Lead
                    </div>
                  )}
                  {tempGooglePin.placeType === 'planned' && (
                    <div style={{ width: '100%', padding: '0.5rem 0.5rem', fontSize: '0.8rem', color: '#1d4ed8', textAlign: 'center', fontWeight: '600', backgroundColor: '#eff6ff', borderBottom: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      🚗 Plan to Visit
                    </div>
                  )}
                  {tempGooglePin.placeType === 'last_visited' && (
                    <div style={{ width: '100%', padding: '0.5rem 0.5rem', fontSize: '0.8rem', color: '#be185d', textAlign: 'center', fontWeight: '600', backgroundColor: '#fdf2f8', borderBottom: '1px solid #fbcfe8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      📍 Last Visited
                    </div>
                  )}
                  <div style={{ padding: '0.75rem 0.25rem' }}>
                    <h4 style={{ margin: '0 0 0.35rem 0', color: '#111827', fontSize: '0.95rem', fontWeight: '700', lineHeight: '1.3' }}>{tempGooglePin.name}</h4>
                    <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#4b5563', lineHeight: '1.4' }}>{tempGooglePin.address}</p>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button 
                      onClick={() => {
                        if (tripStops.find(s => s.cd_no === tempGooglePin.cd_no)) {
                          alert("สถานที่นี้อยู่ในทริปแล้ว");
                          return;
                        }
                        if (tripStops.length >= 24) {
                          alert("เพิ่มจุดแวะได้สูงสุด 24 จุดครับ");
                          return;
                        }
                        setTripStops([...tripStops, tempGooglePin]);
                        setTempGooglePin(null);
                      }}
                      style={{ backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '0.4rem 0.8rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', width: '100%', justifyContent: 'center' }}
                    >
                      <Plus size={14} /> เพิ่มลงในทริป
                    </button>
                    {(() => {
                      const placeId = tempGooglePin.cd_no.replace('GOOGLE_', '');
                      const savedPlaceInfo = savedPlaces.find(p => p.place_id === placeId);
                      
                      if (savedPlaceInfo) {
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <button 
                              onClick={() => {
                                setActiveSurveyPlace({
                                  id: savedPlaceInfo.id,
                                  initialData: savedPlaceInfo.survey_data ? JSON.parse(savedPlaceInfo.survey_data) : null
                                });
                                setShowSurveyModal(true);
                              }}
                              style={{ backgroundColor: '#8b5cf6', color: 'white', border: 'none', padding: '0.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.8rem', fontWeight: 'bold' }}
                            >
                              <ClipboardList size={16} /> ทำ Site Survey
                            </button>
                            
                            {savedPlaceInfo.is_planned == 0 && savedPlaceInfo.is_visited != 1 && (
                              <button 
                                onClick={() => {
                                  updatePlannedDate(savedPlaceInfo.id, null);
                                  setTempGooglePin(null);
                                  alert("เพิ่มลงใน Plan to Visit เรียบร้อยแล้ว");
                                }}
                                style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.8rem', fontWeight: 'bold' }}
                              >
                                <CalendarIcon size={16} /> วางแผนไปเยี่ยม
                              </button>
                            )}
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              {savedPlaceInfo.type !== 'favorite' && (
                                <button 
                                  onClick={() => saveGooglePlace('favorite')}
                                  style={{ flex: 1, backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold' }}
                                >
                                  <Star size={14} fill="#d97706" /> รายการโปรด
                                </button>
                              )}
                              <button 
                                onClick={() => navigate('/business-cards')}
                                style={{ flex: 1, backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold' }}
                              >
                                <Contact size={14} /> เพิ่มนามบัตร
                              </button>
                            </div>
                          </div>
                        );
                      } else {
                        const isLeadPin = tempGooglePin.cd_no && tempGooglePin.cd_no.startsWith('LEAD_');
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button 
                                onClick={() => saveGooglePlace('favorite')}
                                style={{ flex: 1, backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold' }}
                              >
                                <Star size={14} fill="#d97706" /> รายการโปรด
                              </button>
                              <button 
                                onClick={() => saveGooglePlace('new_customer')}
                                style={{ flex: 1, backgroundColor: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold' }}
                              >
                                <UserPlus size={14} /> Sales Lead
                              </button>
                            </div>
                            
                            {!isLeadPin && (
                              <button 
                                onClick={() => setShowNewLeadModal(true)}
                                style={{ backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold', width: '100%' }}
                              >
                                <Megaphone size={14} /> สร้าง New Lead
                              </button>
                            )}
                            <button 
                              onClick={() => navigate('/business-cards')}
                              style={{ backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold', width: '100%' }}
                            >
                              <Contact size={14} /> เพิ่มนามบัตร
                            </button>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
              </InfoWindow>
            </>
          )}
          {userLocation && (
            <AdvancedMarker position={userLocation} title="You are here" zIndex={100}>
              <Pin background="#3b82f6" glyphColor="#ffffff" borderColor="#ffffff" scale={1.2} />
            </AdvancedMarker>
          )}
          <Directions 
            origin={userLocation} 
            tripStops={activeRouteStops} 
            onRouteFound={setRouteInfo} 
            onRouteClear={() => { setRouteInfo(null); setActiveRouteStops([]); }} 
          />
        </MapComponent>
        
        {/* Trip Planner Panel */}
        {tripStops.length > 0 && (
        <div className="card" style={{ 
          position: 'absolute', 
          bottom: '20px', 
          left: '20px', 
          zIndex: 10, 
          width: '320px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          padding: '1rem'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <MapPin size={20} /> วางแผนทริป ({tripStops.length})
            {activeRouteStops.length > 0 && (
              <button 
                onClick={() => setIsTripIsolated(!isTripIsolated)}
                style={{ marginLeft: 'auto', fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', background: isTripIsolated ? '#f1f5f9' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem' }}
              >
                {isTripIsolated ? <Eye size={14} /> : <EyeOff size={14} />} {isTripIsolated ? 'แสดงจุดอื่น' : 'ซ่อนจุดอื่น'}
              </button>
            )}
          </h3>
          
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', paddingRight: '4px', marginBottom: '1rem' }}>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.5rem', backgroundColor: '#f8fafc', minHeight: '100px', flexShrink: 0, marginBottom: routeInfo ? '1rem' : '0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tripStops.map((stop, idx) => (
                  <div key={stop.cd_no} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>{idx + 1}.</strong> {stop.search_name}
                    </div>
                    <button 
                      onClick={() => {
                        const newStops = tripStops.filter(s => s.cd_no !== stop.cd_no);
                        setTripStops(newStops);
                        if (activeRouteStops.length > 0) setActiveRouteStops([]);
                      }}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0 0.2rem' }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
          </div>

          {routeInfo ? (
            <div style={{ backgroundColor: '#eff6ff', padding: '0.75rem', borderRadius: '8px', border: '1px solid #bfdbfe', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.85rem', color: '#1e40af' }}>ระยะทางรวม:</span>
                <span style={{ fontWeight: 'bold', color: '#1e3a8a' }}>{routeInfo.distance}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.85rem', color: '#1e40af' }}>เวลาขับรถรวม:</span>
                <span style={{ fontWeight: 'bold', color: '#1e3a8a' }}>{routeInfo.duration}</span>
              </div>
              
              {routeInfo.legs && routeInfo.legs.length > 0 && (
                <div style={{ marginTop: '0.5rem', marginBottom: '0.75rem', borderTop: '1px dashed #93c5fd', paddingTop: '0.5rem' }}>
                  {routeInfo.legs.map(leg => (
                    <div key={leg.legIndex} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#1e40af', marginBottom: '0.25rem' }}>
                      <span>จุดที่ {leg.legIndex + 1} ➔ {leg.legIndex + 2}:</span>
                      <span>{leg.distance} ({leg.duration})</span>
                    </div>
                  ))}
                </div>
              )}

              <button 
                className="btn btn-primary" 
                style={{ width: '100%', fontSize: '0.85rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.4rem', backgroundColor: '#10b981', borderColor: '#10b981' }}
                onClick={openGoogleMapsApp}
              >
                <Navigation size={16} /> นำทางด้วย Google Maps
              </button>
            </div>
          ) : null}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button 
              className="btn btn-secondary" 
              style={{ flex: 1, fontSize: '0.85rem' }}
              onClick={() => { setTripStops([]); setActiveRouteStops([]); setIsTripIsolated(false); }}
              disabled={tripStops.length === 0}
            >
              ล้างทั้งหมด
            </button>
            <button 
              className="btn btn-primary" 
              style={{ flex: 2, fontSize: '0.85rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.25rem' }}
              onClick={() => {
                if (tripStops.length === 0) return;
                if (!userLocation) {
                  alert("ไม่สามารถคำนวณเส้นทางได้ ❌\n\nระบบไม่พบตำแหน่งปัจจุบันของคุณ กรุณาอนุญาตให้เบราว์เซอร์เข้าถึงตำแหน่ง (Location Permission) ที่สัญลักษณ์ 🔒 บนช่อง URL แล้วรีเฟรชหน้าเว็บใหม่ครับ");
                  return;
                }
                setActiveRouteStops([...tripStops]);
                setIsTripIsolated(true);
              }}
              disabled={tripStops.length === 0 || activeRouteStops.length > 0}
            >
              <Car size={16} /> {activeRouteStops.length > 0 ? 'กำลังคำนวณ...' : 'คำนวณเส้นทางทริป'}
            </button>
          </div>
        </div>
        )}

        {/* Floating Info Card */}
        {selectedFeatureInfo && (
          <div className="card" style={{ 
            position: 'absolute', 
            top: '20px', 
            right: '20px', 
            zIndex: 10, 
            width: '250px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--color-primary)' }}>
                {selectedFeatureInfo.teamName || 'Unknown Team'}
              </h3>
              <button 
                onClick={() => setSelectedFeatureInfo(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 0, lineHeight: 1 }}
              >
                &times;
              </button>
            </div>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text)' }}>
              {selectedFeatureInfo.areaName}
            </p>
          </div>
        )}
        </div>

        {/* Right Sidebar for Lists */}
        <div style={{ width: '280px', display: 'flex', flexDirection: 'column', gap: '0.75rem', overflowY: 'auto' }}>
          
          {/* Favorite Places */}
          <div className="card" style={{ padding: '0.75rem', backgroundColor: '#fffbeb', borderTop: '4px solid #f59e0b' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#b45309', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Star size={16} fill="#f59e0b" color="#f59e0b" /> รายการโปรด ({favorites.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {favorites.length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#92400e', textAlign: 'center', padding: '0.5rem 0' }}>ไม่มีรายการ</div>
              ) : favorites.map(p => (
                <div 
                  key={p.id} 
                  onClick={() => handlePlaceClick(p)}
                  style={{ backgroundColor: 'white', padding: '0.4rem 0.5rem', borderRadius: '4px', border: '1px solid #fde68a', fontSize: '0.8rem', cursor: 'pointer', transition: 'all 0.2s', position: 'relative' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(-2px)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '25px' }}>{p.name}</strong>
                  <div style={{ fontSize: '0.7rem', color: '#92400e', marginTop: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '25px' }}>{p.address}</div>
                  <button 
                    onClick={(e) => handleDeleteFavorite(e, p.id)}
                    style={{ position: 'absolute', top: '50%', right: '5px', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', transition: 'background-color 0.2s' }}
                    title="ลบรายการโปรด"
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          {/* New Leads (Marketing) */}
          <div className="card" style={{ padding: '0.75rem', backgroundColor: '#eef2ff', borderTop: '4px solid #6366f1' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#4338ca', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <UserPlus size={16} color="#6366f1" /> Marketing Lead ({dashboardLeads.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: '180px', overflowY: 'auto', paddingRight: '4px' }}>
              {dashboardLeads.length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#3730a3', textAlign: 'center', padding: '0.5rem 0' }}>ไม่มีรายการ</div>
              ) : dashboardLeads.slice(0, 20).map(l => (
                <div 
                  key={`lead_${l.id}`} 
                  onClick={() => handlePlaceClick(l)}
                  style={{ backgroundColor: 'white', padding: '0.4rem 0.5rem', borderRadius: '4px', border: '1px solid #c7d2fe', fontSize: '0.8rem', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(-2px)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#312e81' }}>{l.company_name_th || l.company_name_en || l.customer_name || 'ไม่ระบุชื่อบริษัท'}</strong>
                  <div style={{ fontSize: '0.7rem', color: '#4338ca', marginTop: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.address || l.province || l.region || l.location || '-'}</div>
                </div>
              ))}
            </div>
            {dashboardLeads.length > 0 && (
              <button 
                onClick={() => navigate('/leads')}
                style={{ marginTop: '0.5rem', backgroundColor: '#6366f1', color: 'white', border: 'none', padding: '0.4rem', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', width: '100%', transition: 'background-color 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4f46e5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6366f1'}
              >
                ดูทั้งหมด...
              </button>
            )}
          </div>

          {/* New Customers */}
          <div className="card" style={{ padding: '0.75rem', backgroundColor: '#f0fdf4', borderTop: '4px solid #10b981' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#047857', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <UserPlus size={16} color="#10b981" /> Sales Lead ({newPlaces.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {newPlaces.length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#065f46', textAlign: 'center', padding: '0.5rem 0' }}>ไม่มีรายการ</div>
              ) : newPlaces.map(p => (
                <div 
                  key={p.id} 
                  onClick={() => handlePlaceClick(p)}
                  style={{ backgroundColor: 'white', padding: '0.4rem 0.5rem', borderRadius: '4px', border: '1px solid #a7f3d0', fontSize: '0.8rem', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(-2px)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</strong>
                  <div style={{ fontSize: '0.7rem', color: '#065f46', marginTop: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.address}</div>
                  <div style={{ fontSize: '0.65rem', color: '#059669', marginTop: '0.2rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '2px' }}><User size={10} /> เพิ่มโดย: {p.saved_by_name || 'Unknown'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Plan to Visit */}
          <div className="card" style={{ padding: '0.75rem', backgroundColor: '#eff6ff', borderTop: '4px solid #3b82f6' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#1d4ed8', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Car size={16} color="#3b82f6" /> Plan to Visit ({tasks.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {tasks.length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#1e3a8a', textAlign: 'center', padding: '0.5rem 0' }}>ไม่มีรายการ</div>
              ) : tasks.map(p => (
                <div 
                  key={p.id} 
                  onClick={() => handlePlaceClick(p)}
                  style={{ backgroundColor: 'white', padding: '0.4rem 0.5rem', borderRadius: '4px', border: '1px solid #bfdbfe', fontSize: '0.8rem', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(-2px)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</strong>
                  <div style={{ fontSize: '0.7rem', color: '#1e3a8a', marginTop: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.address}</div>
                  {travelTimes[p.id] ? (
                    <div style={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 600, marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                      <Car size={10} /> {travelTimes[p.id].duration} ({travelTimes[p.id].distance})
                    </div>
                  ) : !userLocation ? (
                    <div style={{ fontSize: '0.65rem', color: '#ef4444', marginTop: '0.25rem' }}>
                      ⚠️ รอสิทธิ์ Location (พิกัด)
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                      <div className="spinner" style={{ width: '8px', height: '8px', border: '2px solid #e2e8f0', borderTop: '2px solid #94a3b8', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                      กำลังคำนวณเส้นทาง...
                    </div>
                  )}
                  <div style={{ marginTop: '0.5rem' }} onClick={e => e.stopPropagation()}>
                    <label style={{ fontSize: '0.65rem', color: '#64748b', display: 'block', marginBottom: '0.1rem' }}>วันที่คาดว่าจะเข้าพบ:</label>
                    <input 
                      type="date" 
                      value={p.planned_date || ''} 
                      onChange={(e) => updatePlannedDate(p.id, e.target.value)}
                      style={{ fontSize: '0.75rem', padding: '0.25rem', width: '100%', border: '1px solid #bfdbfe', borderRadius: '4px', outline: 'none' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Last Visited Card */}
          <div className="card" style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', borderLeft: '4px solid #6b7280', flex: '0 0 auto' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <CheckCircle size={14} /> Last Visited ({lastVisited.length})
            </h3>
            <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem', paddingRight: '0.2rem' }}>
              {lastVisited.length === 0 && <p style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', margin: '0.5rem 0' }}>ไม่มีรายการ</p>}
              {lastVisited.map(p => (
                <div 
                  key={p.id} 
                  onClick={() => handlePlaceClick(p)}
                  style={{ 
                    backgroundColor: 'white', 
                    padding: '0.4rem 0.5rem', 
                    borderRadius: '4px', 
                    border: '1px solid #e5e7eb', 
                    fontSize: '0.8rem', 
                    cursor: 'pointer', 
                    transition: 'all 0.2s' 
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(-2px)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = '#9ca3af'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                  <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#4b5563' }}>{p.name}</strong>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.address}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {activeSurveyPlace && (
        <SiteSurveyModal 
          isOpen={showSurveyModal}
          onClose={() => {
            setShowSurveyModal(false);
            setActiveSurveyPlace(null);
          }}
          placeId={activeSurveyPlace.id}
          initialData={activeSurveyPlace.initialData}
          onSave={() => {
            fetchSavedPlaces(); // refresh data after save
          }}
        />
      )}

      {/* New Lead Modal */}
      <NewLeadModal
        isOpen={showNewLeadModal}
        onClose={() => setShowNewLeadModal(false)}
        locationData={tempGooglePin}
        user={user}
      />



    </div>
  );
}
